import React from "react";
import { Upload, Modal, Icon, message } from "antd";

const UploadFotos = ({ fileList, setFileList }) => {
    const [previewVisible, setPreviewVisible] = React.useState(false);
    const [previewImage, setPreviewImage] = React.useState("");

    const handleCancel = () => setPreviewVisible(false);

    const handlePreview = async file => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }

        setPreviewImage(file.url || file.preview);
        setPreviewVisible(true);
    };

    const getBase64 = file => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    // Função de compressão de imagem
    const compressImage = (file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = e => {
                const img = new Image();

                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");

                    // Calcular novas dimensões mantendo a proporção
                    let width = img.width;
                    let height = img.height;

                    // Verificar se precisa redimensionar
                    const needsResize = width > maxWidth || height > maxHeight;

                    if (needsResize) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width = Math.floor(width * ratio);
                        height = Math.floor(height * ratio);
                    }

                    // Se não precisa redimensionar e arquivo é pequeno, retornar original
                    if (!needsResize && file.size < 200 * 1024) {
                        // < 200KB
                        resolve(file);
                        return;
                    }

                    canvas.width = width;
                    canvas.height = height;

                    // Desenhar imagem redimensionada com melhor qualidade
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = "high";
                    ctx.drawImage(img, 0, 0, width, height);

                    // Ajustar qualidade dinamicamente
                    let finalQuality = quality;
                    if (file.size > 2 * 1024 * 1024) {
                        // > 2MB
                        finalQuality = 0.7;
                    } else if (file.size > 1 * 1024 * 1024) {
                        // > 1MB
                        finalQuality = 0.75;
                    }

                    // Converter para Blob
                    canvas.toBlob(
                        blob => {
                            if (blob) {
                                // Verificar se a compressão valeu a pena
                                if (blob.size >= file.size * 0.9) {
                                    // Se o tamanho ficou maior ou quase igual, usar original
                                    resolve(file);
                                    return;
                                }

                                const compressedFile = new File([blob], file.name, {
                                    type: file.type,
                                    lastModified: Date.now()
                                });

                                // Log para ver a compressão
                                const originalSizeKB = (file.size / 1024).toFixed(2);
                                const compressedSizeKB = (blob.size / 1024).toFixed(2);
                                const reduction = ((1 - blob.size / file.size) * 100).toFixed(1);

                                resolve(compressedFile);
                            } else {
                                reject(new Error("Erro ao comprimir imagem"));
                            }
                        },
                        file.type === "image/png" ? "image/jpeg" : file.type, // Converter PNG para JPEG para melhor compressão
                        finalQuality
                    );
                };

                img.onerror = () => reject(new Error("Erro ao carregar imagem"));
                img.src = e.target.result;
            };

            reader.onerror = () => reject(new Error("Erro ao ler arquivo"));
            reader.readAsDataURL(file);
        });
    };

    // Modificar o beforeUpload para comprimir
    const handleBeforeUpload = async file => {
        const isImage = file.type.startsWith("image/");

        if (!isImage) {
            message.error("Apenas imagens são permitidas!");
            return Upload.LIST_IGNORE;
        }

        try {
            // Comprimir a imagem
            const compressedFile = await compressImage(
                file,
                1920, // largura máxima
                1080, // altura máxima
                0.8 // qualidade (0.7 a 0.9 recomendado)
            );

            // Preservar o uid original para evitar erros de key
            compressedFile.uid = file.uid;

            // Adicionar manualmente à lista sem fazer upload
            const newFile = {
                uid: file.uid,
                name: compressedFile.name,
                status: "done",
                originFileObj: compressedFile
            };

            setFileList([...fileList, newFile]);

            // Retornar false para prevenir upload automático
            return false;
        } catch (error) {
            console.error("Erro na compressão:", error);
            message.error("Erro ao processar imagem");
            return false;
        }
    };

    const handleChange = info => {
        // Apenas atualiza a lista se for remoção ou ordenação
        // Não atualiza durante a adição (beforeUpload já faz isso)
        if (info.file.status === "removed" || info.fileList.length < fileList.length) {
            setFileList(info.fileList);
        }
    };

    const uploadButton = (
        <div>
            <Icon type="plus" />
            <div className="ant-upload-text">Carregar</div>
        </div>
    );

    return (
        <>
            <Upload
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleChange}
                beforeUpload={handleBeforeUpload}
                customRequest={() => {}} // Previne qualquer tentativa de upload
            >
                {fileList.length >= 3 ? null : uploadButton}
            </Upload>
            <Modal visible={previewVisible} footer={null} onCancel={handleCancel}>
                <img alt="example" style={{ width: "100%" }} src={previewImage} />
            </Modal>
        </>
    );
};

export default UploadFotos;
